/**
 * Tipos e chamadas da venda do PDV.
 *
 * O contrato espelha `api/internal/modulos/vendas/dominio.go` e a seção 1 de
 * `docs/plano/fluxo-de-venda.md`. Três pontos carregam o comportamento da
 * tela:
 *
 * 1. O `sku` que atravessa a fronteira é o CÓDIGO DE BARRAS — a mesma
 *    identidade que o leitor bipa e que `estoque.EntradaAjuste.SKU` já usa.
 *    Não existe "código de SKU" separado neste sistema; o `codigo` do produto
 *    (`CAM-PERF`) NÃO serve aqui.
 * 2. O total é do SERVIDOR. O front soma para MOSTRAR — e é só isso. O serviço
 *    recalcula `subtotal = Σ (preço × quantidade)` a cada venda (INV-08) e
 *    recusa com 422 quando `Σ pagamentos` não fecha exatamente com o total.
 *    Quando o número da tela divergir do da resposta, quem vale é a resposta.
 * 3. Dinheiro é inteiro em centavos em todo o caminho (CONSTITUICAO.md §1).
 *    As duas contas que este arquivo faz — percentual de desconto e valor da
 *    parcela — são aritmética INTEIRA, espelhando `Centavos.Aplicar` e
 *    `dinheiro.Ratear` do Go, e existem para a tela mostrar o número antes de
 *    enviar. Quem decide continua sendo o servidor.
 */
import { api } from './api'
import { analisar, type Centavos } from './dinheiro'

/** Forma de pagamento — lista fechada, a mesma do `check` da migração 0012. */
export type Forma = 'dinheiro' | 'pix' | 'debito' | 'credito' | 'crediario'

export type StatusVenda = 'concluida' | 'cancelada'

// ------------------------------------------------------------------ entrada

/** Uma linha do carrinho no corpo de POST /vendas. */
export type ItemPedido = {
  /** Código de barras do SKU. */
  sku: string
  quantidade: number
  /**
   * Ausente vale o preço do SKU no catálogo. Presente e diferente do preço de
   * tabela, o servidor trata como desconto no item — e aplica a mesma regra de
   * limite do desconto da venda.
   */
  preco_unitario_centavos?: Centavos
}

export type PagamentoPedido = {
  forma: Forma
  valor_centavos: Centavos
  /** Ausente é 1 (à vista). Só crédito e crediário aceitam mais de uma. */
  parcelas?: number
  /** Quanto o cliente entregou em espécie. Só existe em dinheiro. */
  recebido_centavos?: Centavos
}

export type PedidoVenda = {
  /** Código `C0001`. Obrigatório quando há pagamento em crediário. */
  cliente?: string
  itens: ItemPedido[]
  desconto_centavos?: Centavos
  pagamentos: PagamentoPedido[]
  observacao?: string
}

// ------------------------------------------------------------------- saída

export type ClienteResumo = {
  codigo: string
  nome: string
}

export type SKUResumo = {
  codigo_barras: string
  produto: string
  cor: string
  tamanho: string
}

export type ItemVenda = {
  numero_item: number
  sku: SKUResumo
  quantidade: number
  preco_unitario_centavos: Centavos
  total_centavos: Centavos
}

export type PagamentoVenda = {
  forma: Forma
  forma_rotulo: string
  valor_centavos: Centavos
  parcelas: number
  /** Só vem em dinheiro. */
  recebido_centavos?: Centavos
}

/** Uma `conta_receber` nascida desta venda — o carnê sai junto do cupom. */
export type ParcelaVenda = {
  codigo: string
  numero_parcela: number
  total_parcelas: number
  valor_centavos: Centavos
  /** `2026-10-05` — data pura, sem hora e sem fuso. */
  vencimento: string
  forma: Forma
  forma_rotulo: string
  status: string
  status_rotulo: string
}

export type Venda = {
  /** `V0001` — a identidade que o balcão dita ao telefone. */
  codigo: string
  cliente?: ClienteResumo
  vendedor?: string
  status: StatusVenda
  status_rotulo: string
  subtotal_centavos: Centavos
  desconto_centavos: Centavos
  total_centavos: Centavos
  /** Σ (recebido − valor) das formas em dinheiro. */
  troco_centavos: Centavos
  concluida_em: string
  cancelada_em?: string
  motivo_cancelamento?: string
  observacao?: string
  itens: ItemVenda[]
  pagamentos: PagamentoVenda[]
  parcelas: ParcelaVenda[]
  parcelas_geradas: number
}

// ---------------------------------------------------------------- chamadas

/**
 * Registra a venda. É a única escrita do PDV.
 *
 * O `pin` viaja em cabeçalho (ver `api.ts`) e existe para o reenvio depois de
 * um 409 `requires_pin` — desconto acima do limite do papel, por exemplo. A
 * tela nunca decide se o PIN é necessário: ela reage ao 409 do porteiro
 * (CONSTITUICAO.md §4.1).
 */
export function registrarVenda(pedido: PedidoVenda, pin?: string): Promise<Venda> {
  return api.post<Venda>('/vendas', pedido, pin)
}

/**
 * O que a tela de faturar condicional monta — subtotal e itens ficam de
 * fora: já foram congelados na abertura da condicional (ver o comentário de
 * pacote em `api/internal/modulos/condicional/dominio.go`), e o servidor
 * recalcula tudo a partir do código.
 */
export type DadosFaturamento = {
  descontoCentavos?: Centavos
  pagamentos: PagamentoPedido[]
  observacao?: string
}

/**
 * Fatura uma condicional — `POST /vendas` com `condicional` preenchido e SEM
 * `itens`.
 *
 * NÃO é uma rota do módulo `condicional`: é literalmente a mesma escrita do
 * PDV, e por isso mora aqui, ao lado de `registrarVenda`, e não em
 * `plataforma/condicional.ts`. Os itens, o preço e o custo vêm CONGELADOS da
 * abertura da condicional — mandar `itens` junto com `condicional` é o 422
 * que o servidor recusa por contradição (ver `docs/de-para.md`, "Venda
 * condicional").
 */
export function faturarCondicional(
  codigo: string,
  dados: DadosFaturamento,
  pin?: string,
): Promise<Venda> {
  return api.post<Venda>(
    '/vendas',
    {
      condicional: codigo,
      desconto_centavos: dados.descontoCentavos || undefined,
      pagamentos: dados.pagamentos,
      observacao: dados.observacao,
    },
    pin,
  )
}

// ---------------------------------------------------------------- catálogo

export type DescricaoDaForma = {
  chave: Forma
  rotulo: string
  /** Se aceita mais de uma parcela — o mesmo `PermiteParcelar` do Go. */
  parcela: boolean
  /** Se o servidor exige cliente identificado (crediário é a única). */
  exigeCliente: boolean
  /** Se a forma tem valor recebido e troco. */
  recebido: boolean
}

const DESCRICAO: Record<Forma, DescricaoDaForma> = {
  dinheiro: { chave: 'dinheiro', rotulo: 'Dinheiro', parcela: false, exigeCliente: false, recebido: true },
  pix: { chave: 'pix', rotulo: 'PIX', parcela: false, exigeCliente: false, recebido: false },
  debito: { chave: 'debito', rotulo: 'Débito', parcela: false, exigeCliente: false, recebido: false },
  credito: { chave: 'credito', rotulo: 'Crédito', parcela: true, exigeCliente: false, recebido: false },
  crediario: { chave: 'crediario', rotulo: 'Crediário', parcela: true, exigeCliente: true, recebido: false },
}

/** A ordem em que o balcão vê as formas — a mesma do protótipo. */
export const FORMAS: DescricaoDaForma[] = [
  DESCRICAO.dinheiro,
  DESCRICAO.pix,
  DESCRICAO.debito,
  DESCRICAO.credito,
  DESCRICAO.crediario,
]

export function descricaoDaForma(forma: Forma): DescricaoDaForma {
  return DESCRICAO[forma]
}

/** O teto do `check` da migração 0012. */
export const LIMITE_PARCELAS = 24

/** As parcelas que o balcão oferece com um toque — as mesmas do protótipo. */
export const PARCELAS_OFERECIDAS = [1, 2, 3, 4, 6, 10]

// ------------------------------------------------------------------ contas

/**
 * O desconto que a pessoa digitou, já resolvido em centavos.
 *
 * A spec 05 aceita as duas formas no MESMO campo: `120,00` é valor e `10%` é
 * percentual. Separar em dois campos obrigaria escolher antes de digitar, e
 * quem está no balcão digita o que o cliente pediu.
 */
export type ResultadoDesconto = {
  /** Quanto abater. Zero quando o campo está vazio ou inválido. */
  centavos: Centavos
  /** Percentual em ponto-base (1000 = 10 %), quando foi digitado com `%`. */
  pontos?: number
  /** Mensagem em pt-BR quando o texto não serve. */
  erro?: string
}

/**
 * Interpreta o campo de desconto contra o subtotal.
 *
 * O percentual reaproveita `dinheiro.analisar`, que devolve centésimos: "12,5"
 * vira 1250, que é exatamente o ponto-base de 12,5 %. Uma conversão a menos é
 * uma chance a menos de o percentual e o dinheiro divergirem de arredondamento.
 */
export function interpretarDesconto(texto: string, subtotal: Centavos): ResultadoDesconto {
  const limpo = texto.trim()
  if (limpo === '') return { centavos: 0 }

  if (limpo.includes('%')) {
    const pontos = analisar(limpo.replace('%', '').trim())
    if (pontos === null || pontos < 0) {
      return { centavos: 0, erro: 'Percentual inválido. Escreva algo como 10%.' }
    }
    if (pontos > 10000) {
      return { centavos: 0, pontos, erro: 'O desconto não pode passar de 100%.' }
    }
    return { centavos: aplicarPontos(subtotal, pontos), pontos }
  }

  const centavos = analisar(limpo)
  if (centavos === null || centavos < 0) {
    return { centavos: 0, erro: 'Valor inválido. Escreva 120,00 ou 10%.' }
  }
  if (centavos > subtotal) {
    return { centavos: 0, erro: 'O desconto não pode passar do valor da mercadoria.' }
  }
  return { centavos }
}

/**
 * Percentual sobre valor — a mesma conta de `Centavos.Aplicar` no Go.
 *
 * Meio-para-cima em valor absoluto, aritmética inteira do começo ao fim
 * (CONSTITUICAO.md §1, "Arredondamento"). Ter duas regras de arredondamento é
 * ter uma delas errada, e a divergência aparece de um centavo em uma venda
 * qualquer no fim do mês.
 */
export function aplicarPontos(valor: Centavos, pontos: number): Centavos {
  if (valor === 0 || pontos === 0) return 0
  const negativo = valor < 0 !== pontos < 0
  const bruto = Math.abs(valor) * Math.abs(pontos)
  const resultado = Math.floor((bruto + 5000) / 10000)
  return negativo ? -resultado : resultado
}

/**
 * Divide o valor em `n` parcelas — a mesma regra de `dinheiro.Ratear`.
 *
 * O resto vai um centavo por parte, da primeira à última, e `Σ partes ≡ total`
 * (INV-01). Existe aqui para o chip de crédito mostrar "3× de R$ 63,30" antes
 * de enviar; quem gera as parcelas de verdade é o servidor, e é o valor DELE
 * que vai para o carnê.
 */
export function ratear(total: Centavos, n: number): Centavos[] {
  if (n <= 0) return []

  const base = Math.trunc(total / n)
  let resto = total % n
  let passo = 1
  if (resto < 0) {
    passo = -1
    resto = -resto
  }

  return Array.from({ length: n }, (_, indice) => (indice < resto ? base + passo : base))
}

/** O valor da primeira parcela — o que o chip "3×" mostra. */
export function valorDaParcela(total: Centavos, n: number): Centavos {
  return ratear(total, n)[0] ?? 0
}

/** Soma inteira dos pagamentos montados na tela. */
export function somarPagamentos(pagamentos: PagamentoPedido[]): Centavos {
  return pagamentos.reduce((soma, pagamento) => soma + pagamento.valor_centavos, 0)
}
